import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackGameComplete, trackGameRestart } from '../utils/analytics';
import './GameStyles.css';

const DIFFICULTIES = {
  easy: { label: 'Easy', rows: 8, cols: 8, mines: 10 },
  medium: { label: 'Medium', rows: 10, cols: 10, mines: 18 },
  hard: { label: 'Hard', rows: 12, cols: 12, mines: 28 },
};

const directions = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

const makeKey = (row, col) => `${row}-${col}`;

const createBoard = (difficulty) => {
  const config = DIFFICULTIES[difficulty];
  const mineKeys = new Set();

  while (mineKeys.size < config.mines) {
    mineKeys.add(makeKey(Math.floor(Math.random() * config.rows), Math.floor(Math.random() * config.cols)));
  }

  const board = Array.from({ length: config.rows }, (_, row) =>
    Array.from({ length: config.cols }, (_, col) => ({
      row,
      col,
      key: makeKey(row, col),
      hasMine: mineKeys.has(makeKey(row, col)),
      adjacent: 0,
      revealed: false,
      flagged: false,
    }))
  );

  return board.map((row) =>
    row.map((cell) => {
      if (cell.hasMine) {
        return cell;
      }

      const adjacent = directions.reduce((count, [dx, dy]) => {
        const neighbor = board[cell.row + dy]?.[cell.col + dx];
        return count + (neighbor?.hasMine ? 1 : 0);
      }, 0);

      return { ...cell, adjacent };
    })
  );
};

const revealFrom = (board, startCell) => {
  const next = board.map((row) => row.map((cell) => ({ ...cell })));
  const queue = [startCell];
  const seen = new Set();

  while (queue.length > 0) {
    const cell = queue.shift();
    if (!cell || seen.has(cell.key)) {
      continue;
    }

    seen.add(cell.key);
    const current = next[cell.row][cell.col];
    if (current.flagged || current.revealed) {
      continue;
    }

    current.revealed = true;

    if (current.adjacent === 0 && !current.hasMine) {
      directions.forEach(([dx, dy]) => {
        const neighbor = next[current.row + dy]?.[current.col + dx];
        if (neighbor && !neighbor.hasMine && !neighbor.flagged && !neighbor.revealed) {
          queue.push(neighbor);
        }
      });
    }
  }

  return next;
};

const revealAllMines = (board) =>
  board.map((row) =>
    row.map((cell) => ({
      ...cell,
      revealed: cell.revealed || cell.hasMine,
    }))
  );

const getStatus = (board) => {
  const cells = board.flat();
  const safeCells = cells.filter((cell) => !cell.hasMine);
  const revealedSafeCells = safeCells.filter((cell) => cell.revealed);

  return {
    flags: cells.filter((cell) => cell.flagged).length,
    won: safeCells.length > 0 && safeCells.length === revealedSafeCells.length,
  };
};

const MinesGarden = ({ game }) => {
  const [difficulty, setDifficulty] = useState('easy');
  const [board, setBoard] = useState(() => createBoard('easy'));
  const [seconds, setSeconds] = useState(0);
  const [flagMode, setFlagMode] = useState(false);
  const [status, setStatus] = useState('playing');

  const config = DIFFICULTIES[difficulty];
  const boardStatus = useMemo(() => getStatus(board), [board]);

  const restart = useCallback((nextDifficulty = difficulty) => {
    setDifficulty(nextDifficulty);
    setBoard(createBoard(nextDifficulty));
    setSeconds(0);
    setFlagMode(false);
    setStatus('playing');
    trackGameRestart(game);
  }, [difficulty, game]);

  useEffect(() => {
    if (status !== 'playing') {
      return undefined;
    }

    const timer = setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [status]);

  useEffect(() => {
    if (status === 'playing' && boardStatus.won) {
      setStatus('won');
      trackGameComplete(game, { result: 'won', seconds, difficulty });
    }
  }, [boardStatus.won, difficulty, game, seconds, status]);

  const toggleFlag = (cell) => {
    if (status !== 'playing' || cell.revealed) {
      return;
    }

    setBoard((current) =>
      current.map((row) =>
        row.map((item) => (item.key === cell.key ? { ...item, flagged: !item.flagged } : item))
      )
    );
  };

  const revealCell = (cell) => {
    if (status !== 'playing' || cell.flagged || cell.revealed) {
      return;
    }

    if (cell.hasMine) {
      setBoard((current) => revealAllMines(current));
      setStatus('lost');
      trackGameComplete(game, { result: 'lost', seconds, difficulty });
      return;
    }

    setBoard((current) => revealFrom(current, cell));
  };

  const handleCellClick = (cell) => {
    if (flagMode) {
      toggleFlag(cell);
      return;
    }

    revealCell(cell);
  };

  const handleContextMenu = (event, cell) => {
    event.preventDefault();
    toggleFlag(cell);
  };

  const minesLeft = Math.max(config.mines - boardStatus.flags, 0);

  return (
    <div className="playable-game">
      <div className="difficulty-row" aria-label="Difficulty">
        {Object.entries(DIFFICULTIES).map(([key, option]) => (
          <button
            className={`difficulty-button ${difficulty === key ? 'active' : ''}`}
            key={key}
            onClick={() => restart(key)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="game-toolbar">
        <div className="game-stats">
          <span className="stat-pill">Mines {minesLeft}</span>
          <span className="stat-pill">Time {seconds}s</span>
        </div>
        <div className="game-actions">
          <button
            className={`game-action-button ${flagMode ? '' : 'secondary'}`}
            onClick={() => setFlagMode((value) => !value)}
            type="button"
          >
            {flagMode ? 'Flag Mode On' : 'Flag Mode'}
          </button>
          <button className="game-action-button" onClick={() => restart()} type="button">
            Restart
          </button>
        </div>
      </div>

      {status === 'won' && <div className="game-status">Garden cleared in {seconds} seconds.</div>}
      {status === 'lost' && <div className="game-status">A mine bloomed. Restart for a fresh board.</div>}

      <div
        className={`mines-board mines-${difficulty}`}
        style={{ gridTemplateColumns: `repeat(${config.cols}, 1fr)` }}
        aria-label="Mines Garden board"
      >
        {board.flat().map((cell) => {
          const classes = ['mines-cell'];
          if (cell.revealed) classes.push('revealed');
          if (cell.flagged) classes.push('flagged');
          if (cell.revealed && cell.hasMine) classes.push('mine');
          if (cell.revealed && cell.adjacent > 0) classes.push(`count-${cell.adjacent}`);

          return (
            <button
              aria-label={`Row ${cell.row + 1}, column ${cell.col + 1}`}
              className={classes.join(' ')}
              key={cell.key}
              onClick={() => handleCellClick(cell)}
              onContextMenu={(event) => handleContextMenu(event, cell)}
              type="button"
            >
              {cell.revealed && cell.hasMine ? 'X' : ''}
              {cell.revealed && !cell.hasMine && cell.adjacent > 0 ? cell.adjacent : ''}
              {!cell.revealed && cell.flagged ? 'F' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MinesGarden;
